/**
 * Singleton state manager for NeighborhoodModule commute persistence
 *
 * This solves the architecture issue where NeighborhoodModule and NeighborhoodTrigger
 * are rendered separately by the module system. Each component gets its own provider instance,
 * but they share state through this singleton manager.
 */

import type { NeighborhoodActions, NeighborhoodState } from '../types';
import { initialNeighborhoodState } from '../types';
import type { CommuteDestination, CommuteTime, TravelMode } from '../utils/commuteTypes';
import { calculateCommuteTimes as defaultCalculateCommuteTimes } from '../utils/routeCalculation';

type StateChangeListener = (state: NeighborhoodState) => void;

type CalculateCommuteTimesFn = (
  origin: { lat: number; lng: number },
  destination: CommuteDestination,
) => Promise<Array<CommuteTime>>;

export class NeighborhoodStateManager {
  private state: NeighborhoodState;
  private listeners: Set<StateChangeListener> = new Set();
  private requestSeq = 0;
  private inFlightKey: string | null = null;
  private calculateCommuteTimesFn: CalculateCommuteTimesFn;

  constructor(calculateCommuteTimesFn: CalculateCommuteTimesFn = defaultCalculateCommuteTimes) {
    this.state = initialNeighborhoodState;
    this.calculateCommuteTimesFn = calculateCommuteTimesFn;
  }

  // Subscribe to state changes
  subscribe(listener: StateChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Get current state
  getState(): NeighborhoodState {
    return this.state;
  }

  // Update state and notify listeners
  private setState(updater: (prevState: NeighborhoodState) => NeighborhoodState): void {
    this.state = updater(this.state);
    this.listeners.forEach((listener) => listener(this.state));
  }

  // Action creators that update the singleton state
  getActions(): NeighborhoodActions {
    return {
      setCommuteDestination: (destination: CommuteDestination) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            destination,
            inputValue: destination.name,
            status: 'idle',
            error: null,
          },
        }));
      },

      setCommuteTimes: (times: Array<CommuteTime>, listingId?: string) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            times,
            status: 'success',
            error: null,
            lastCalculated: new Date(),
            calculatedForListingId: listingId ?? prev.commute.calculatedForListingId,
          },
        }));
      },

      setSelectedMode: (mode: TravelMode | undefined) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            selectedMode: mode,
          },
        }));
      },

      setInputValue: (value: string) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            inputValue: value,
          },
        }));
      },

      setCommuteStatus: (status: NeighborhoodState['commute']['status']) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            status,
          },
        }));
      },

      setCommuteError: (error: string) => {
        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            status: 'error',
            error,
          },
        }));
      },

      clearCommute: () => {
        // Invalidate any in-flight calculation so it doesn't commit results
        // to the cleared state, and reset the coalesce key so a future recalc
        // for the same listing+destination isn't blocked.
        ++this.requestSeq;
        this.inFlightKey = null;
        this.setState((prev) => ({
          ...prev,
          commute: {
            destination: null,
            times: [],
            selectedMode: undefined,
            inputValue: '',
            status: 'idle',
            error: null,
            lastCalculated: null,
            calculatedForListingId: undefined,
          },
        }));
      },

      setViewMode: (mode) => {
        this.setState((prev) => ({
          ...prev,
          viewMode: mode,
        }));
      },

      recalcForListing: async ({ origin, listingId, destination }) => {
        const current = this.state.commute;
        const targetDestination = destination ?? current.destination;
        if (!targetDestination) return;

        if (current.calculatedForListingId === listingId && current.status === 'success') {
          return;
        }

        // Coalesce concurrent calls for the same listing+destination so multiple
        // mounted entry points (collapsed module, spoke, etc.) only fire one network
        // request. A new key (different listing or destination) supersedes the old one.
        const destinationKey = targetDestination.placeId || `${targetDestination.lat},${targetDestination.lng}`;
        const inFlightKey = `${listingId}|${destinationKey}`;
        if (this.inFlightKey === inFlightKey) return;
        this.inFlightKey = inFlightKey;

        const mySeq = ++this.requestSeq;

        this.setState((prev) => ({
          ...prev,
          commute: {
            ...prev.commute,
            status: 'calculating',
            error: null,
          },
        }));

        let times: Array<CommuteTime>;
        try {
          times = await this.calculateCommuteTimesFn(origin, targetDestination);
        } catch (error) {
          if (mySeq !== this.requestSeq) return;
          this.inFlightKey = null;
          this.setState((prev) => ({
            ...prev,
            commute: {
              ...prev.commute,
              status: 'error',
              error: 'Unable to calculate commute times. Please try again.',
            },
          }));
          return;
        }

        if (mySeq !== this.requestSeq) return;
        this.inFlightKey = null;

        this.setState((prev) => {
          const nextSelectedMode = prev.commute.selectedMode ?? (times.length > 0 ? times[0].mode : undefined);
          return {
            ...prev,
            commute: {
              ...prev.commute,
              times,
              status: 'success',
              error: null,
              lastCalculated: new Date(),
              calculatedForListingId: listingId,
              selectedMode: nextSelectedMode,
            },
          };
        });
      },
    };
  }
}

// Singleton instance - shared across all provider instances
const neighborhoodStateManager = new NeighborhoodStateManager();

export default neighborhoodStateManager;
